/**
 * MembersCouncil Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A specialized people grid component for displaying Council members with IFIA branding.
 * Features include:
 * - Dynamic color mapping for member categories based on representation
 * - Configurable filtering system for member categories
 * - 5-column responsive grid layout optimized for council display
 * - Role information display for each member
 * - Council-specific modal integration
 * - IFIA brand color scheme with automatic color assignment
 * - Production-ready with comprehensive member data handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of member objects with representing, name, role, and other details
 * @param {boolean} [props.showFilters=true] - Whether to show category filter controls
 * @returns {JSX.Element} Members council grid component
 */
import React, { useMemo } from 'react';
import PeopleGrid from '../Common/PeopleGrid';

/**
 * MembersCouncil Component
 * 
 * Displays council members in a grid layout with filtering and color-coded categories.
 * Optimized for performance with memoized color mapping and minimal re-renders.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Member data array
 * @param {boolean} [props.showFilters=true] - Show filter controls
 * @returns {JSX.Element} Members council component
 */
const MembersCouncil = ({ data, showFilters = true }) => {
    // Memoize color mapping based on stable data representation
    const colorMap = useMemo(() => {
        if (!data || data.length === 0) return {};
        
        const colors = [
            'var(--ifia-green)',
            'var(--ifia-orange)',
            'var(--ifia-blue)',
            'var(--ifia-purple)',
            'var(--ifia-dark-green)'
        ];
        
        // Use sorted unique values for consistent color assignment
        const uniqueFilter = [...new Set(data.map(member => member.representing))].sort();
        return uniqueFilter.reduce((acc, category, index) => {
            acc[category] = colors[index % colors.length];
            return acc;
        }, {});
    }, [data?.length, data?.map(m => m.representing).join(',')]);

    return (
        <PeopleGrid 
            data={data}
            filterField="representing"
            colorMap={colorMap}
            gridColumns={5}
            showRole={true}
            showFilters={showFilters}
            modalType="council"
        />
    );
};

export default MembersCouncil;